import { toast } from "react-toastify";
import i18n from "i18next";
import { DocumentData, InfoDocumentale, ListaAttestati } from "../../types";
import {
  ATTESTATO_DOC_TYPE_ID,
  BE_BASE_URL,
  BE_CERTIFICATES_ENDPOINT,
  BE_CERTIFICATES_ZIP_ENDPOINT,
  BE_EMPLOYEE_ENDPOINT,
} from "../../utils/config";
import { axiosInstance } from "../../provider/authProvider";
import { goToPage } from "../../utils/utils";
import { nomeFileScaricato } from "../../utils/nomeFileScaricato";
import {
  nomeFileArchivioAttestati,
  nomeFileAttestato,
} from "./nomeFile";
import { LISTA_VUOTA, leggiListaAttestati } from "./involucro";

// L'intera lista attestati dell'azienda, corsi mancanti compresi, nell'ordine
// canonico del backend e con lo stato già calcolato: il frontend non riordina
// e non ricalcola. Paginazione e ordinamento sono accettati e ignorati dal
// backend, quindi non si mandano. Senza lista la pagina non ha nulla da
// mostrare: l'errore porta alla pagina 500.
//
// Torna anche `troncata`, che il backend dichiara quando l'azienda ha piu'
// attestati di quanti ne abbia potuti consegnare: chi guarda la pagina deve
// sapere che sta leggendo un elenco parziale. In caso di errore vale `false`,
// perche' una chiamata fallita non e' un elenco incompleto.
export function getCertificates(): Promise<ListaAttestati> {
  return axiosInstance
    .get<InfoDocumentale>(BE_BASE_URL + BE_CERTIFICATES_ENDPOINT, {
      params: { dTId: ATTESTATO_DOC_TYPE_ID },
    })
    .then((response) => leggiListaAttestati(response.data))
    .catch(() => {
      goToPage("500");
      return LISTA_VUOTA;
    });
}

// Gli attestati intestati a un dipendente, per la sua scheda di anagrafica: il
// backend li consegna tutti, cessato compreso, mentre dalla lista e dai conteggi
// toglie quelli di chi non è più in forza. È l'unico punto da cui gli attestati
// di un cessato si vedono e si scaricano.
//
// L'operatore sta lavorando dentro la modale e ci resta: un errore non porta via
// la pagina, lascia un toast e la sezione vuota.
export function getCertificatesByEmployee(
  employeeId: number
): Promise<DocumentData[]> {
  return axiosInstance
    .get<InfoDocumentale>(
      `${BE_BASE_URL}${BE_EMPLOYEE_ENDPOINT}${employeeId}/certificates`,
      { params: { dTId: ATTESTATO_DOC_TYPE_ID } }
    )
    .then((response) =>
      response.data && response.data.ok ? response.data.content.data : []
    )
    .catch(() => {
      toast.error(i18n.t("attestati-dipendente-errore"));
      return [];
    });
}

/*
 Scarica più attestati come un unico archivio zip, preparato dal backend.

 Non sono N download uno dopo l'altro: il browser ostacola le raffiche, i file si sparpagliano
 nella cartella Download e chi li raccoglie per un'ispezione deve poi rimetterli insieme a mano.
 L'archivio invece arriva già nominato per la persona a cui appartiene.

 Un archivio che non si riesce a comporre non è un mezzo archivio: il backend risponde con un
 errore e qui resta un toast, con l'operatore dov'era.
*/
export function downloadCertificatesZip(
  attestati: DocumentData[]
): Promise<void> {
  return axiosInstance
    .get<Blob>(BE_BASE_URL + BE_CERTIFICATES_ZIP_ENDPOINT, {
      params: { ids: attestati.map((attestato) => attestato.id).join(",") },
      responseType: "blob",
    })
    .then((response) => {
      salvaFile(
        response.data,
        nomeFileScaricato(
          response.headers?.["content-disposition"],
          nomeFileArchivioAttestati(attestati)
        )
      );
    })
    .catch(() => {
      toast.error(i18n.t("attestati-errore-archivio"));
    });
}

// Consegna un blob al browser come file scaricato. L'ancora finta è l'unico modo di dare un
// nome a un download che nasce da una risposta XHR.
//
// Il nome è quello dell'intestazione Content-Disposition, se arriva, altrimenti quello composto
// qui dai metadati. Oggi il backend non espone quell'intestazione per gli attestati
// (Access-Control-Expose-Headers), quindi vale sempre il secondo; passa comunque da
// nomeFileScaricato perché un nome letto dall'intestazione sia letto in un modo solo.
function salvaFile(contenuto: Blob, nome: string): void {
  const href = URL.createObjectURL(contenuto);
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", nome);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

// Scarica il file dell'attestato e lo consegna al browser come download. Il nome con cui
// arriva sul computer non è quello archiviato in DocuManager ma quello parlante costruito
// dai metadati (nomeFileAttestato): chi scarica sei attestati per un'ispezione deve poterli
// distinguere nella cartella Download.
export function downloadCertificate(attestato: DocumentData): Promise<void> {
  return axiosInstance
    .get<Blob>(`${BE_BASE_URL}${BE_CERTIFICATES_ENDPOINT}/${attestato.id}`, {
      responseType: "blob",
    })
    .then((response) => {
      salvaFile(
        response.data,
        nomeFileScaricato(
          response.headers?.["content-disposition"],
          nomeFileAttestato(attestato)
        )
      );
    })
    .catch(() => {
      toast.error(i18n.t("attestati-errore-download"));
    });
}
